import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import type { PublicMilestone } from '../../types';
import { parseEventDate } from '../../utils/eventDate';
import { PHOTO_ACCEPT } from '../../utils/photoUpload';

export function PublicMilestonePage() {
  const { token } = useParams<{ token: string }>();
  const [milestone, setMilestone] = useState<PublicMilestone | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const [contributorName, setContributorName] = useState('');
  const [message, setMessage] = useState('');
  const [suggestedDate, setSuggestedDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`/api/events/public/milestones/${token}`)
      .then(r => setMilestone(r.data))
      .catch(() => setNotFound(true));
  }, [token]);

  useEffect(() => {
    if (!lightbox) return;
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [lightbox]);

  async function handleContribute(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setSubmitError('Please choose a photo'); return; }
    setSubmitError('');
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('photo', file);
      if (contributorName.trim()) form.append('contributorName', contributorName.trim());
      if (message.trim()) form.append('message', message.trim());
      if (suggestedDate) form.append('suggestedDate', suggestedDate);
      await axios.post(`/api/events/public/milestones/${token}/contribute`, form);
      setSubmitted(true);
    } catch (err: unknown) {
      setSubmitError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to submit photo');
    } finally {
      setSubmitting(false);
    }
  }

  if (notFound) return (
    <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center p-4">
      <div className="text-center">
        <p className="text-slate-500 mb-4">This link is no longer available.</p>
        <Link to="/login" className="text-indigo-600 hover:underline text-sm">Sign in to EventsCarousel</Link>
      </div>
    </div>
  );

  if (!milestone) return (
    <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center">
      <div className="w-6 h-6 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
    </div>
  );

  const thenPhoto = milestone.events[0]?.photos[0] ?? null;
  const lastEvent = milestone.events[milestone.events.length - 1];
  const nowPhoto = lastEvent?.photos[lastEvent.photos.length - 1] ?? null;

  return (
    <div className="min-h-screen bg-[#F8F9FF]">
      <header className="bg-white border-b border-indigo-50 sticky top-0 z-40">
        <div className="px-4 lg:px-8 h-[62px] flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-[34px] h-[34px] rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
              </svg>
            </div>
            <span className="text-[1.05rem] font-bold tracking-tight text-slate-900">EventsCarousel</span>
          </Link>
          <Link to="/register" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            Join EventsCarousel
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 lg:px-8 lg:py-10">
        <div className="mb-8 text-center">
          <span className="text-2xl">✨</span>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">{milestone.title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {milestone.totalPhotos} moment{milestone.totalPhotos === 1 ? '' : 's'} across {milestone.events.length} event{milestone.events.length === 1 ? '' : 's'}
          </p>
        </div>

        {thenPhoto && nowPhoto && (
          <section data-testid="public-then-now" className="mb-10 grid grid-cols-2 gap-4">
            <div className="rounded-xl overflow-hidden bg-white shadow-sm border border-slate-100 aspect-square">
              <img src={thenPhoto.filePath} alt="Then" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-white shadow-sm border border-slate-100 aspect-square">
              <img src={nowPhoto.filePath} alt="Now" className="w-full h-full object-cover" />
            </div>
          </section>
        )}

        {milestone.events.map(event => (
          <section key={event.id} className="mb-10">
            <h2 className="text-base font-semibold text-slate-700 mb-1">{event.title}</h2>
            <p className="text-xs text-slate-400 mb-4">
              {[event.eventDate ? format(parseEventDate(event.eventDate), 'MMMM d, yyyy') : null, event.location].filter(Boolean).join(' · ')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {event.photos.map(photo => (
                <div key={photo.id} className="rounded-xl overflow-hidden bg-white shadow-sm border border-slate-100 aspect-square">
                  <img
                    src={photo.filePath}
                    alt=""
                    className="w-full h-full object-cover cursor-zoom-in hover:scale-105 transition-transform duration-200"
                    onClick={() => setLightbox(photo.filePath)}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="bg-white rounded-2xl border border-indigo-100 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">📤 Add a photo to this story</h3>
          {submitted ? (
            <p className="text-sm text-emerald-600">Thanks — this will appear once it's been reviewed.</p>
          ) : (
            <form onSubmit={handleContribute} className="space-y-3 mt-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={contributorName}
                  onChange={e => setContributorName(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="date"
                  aria-label="Roughly when was this taken?"
                  value={suggestedDate}
                  onChange={e => setSuggestedDate(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <input
                type="text"
                placeholder="A short caption (optional)"
                value={message}
                onChange={e => setMessage(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="file"
                accept={PHOTO_ACCEPT}
                aria-label="Photo"
                onChange={e => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm"
              />
              {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Submitting…' : 'Submit Photo'}
              </button>
            </form>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-indigo-100 p-6 text-center mt-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Preserve your own family memories</h3>
          <p className="text-sm text-slate-500 mb-4">EventsCarousel is a private archive for photos, voices, and stories — shared only with the people you choose.</p>
          <Link to="/register" className="inline-block px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200">
            Create your free archive
          </Link>
        </div>
      </main>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="" className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out select-none" />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl flex items-center justify-center"
          >×</button>
        </div>
      )}
    </div>
  );
}
